import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DepositInvoiceAmountType } from '@features/invoices/enums/depositInvoiceAmountType'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useFetchInvoice } from '@features/invoices/hooks/useFetchInvoice'
import { useFetchQuote } from '@features/invoices/hooks/useFetchQuote'
import { isDocumentTypeInvoice } from '@features/invoices/utils/isDocumentTypeInvoice'
import { isDocumentTypeQuote } from '@features/invoices/utils/isDocumentTypeQuote'
import { useVatRates } from '@features/vatRates/contexts/vatRatesContext'
import { useFilteredVatRates } from '@features/vatRates/hooks/useFilteredVatRates'

import { invoiceEditorFromQuoteVariants } from '../../constants/variants/invoiceEditorFromQuoteVariants'
import { useInvoiceEditorDepositModal } from '../../contexts/invoiceEditorDepositModalContext'
import { InvoiceEditorVariant } from '../../enums/invoiceEditorVariant'
import { getDepositLinesFromPersistedRowLines } from '../../utils/getDepositLinesFromPersistedRowLines'
import { getDepositLinesFromRowLines } from '../../utils/getDepositLinesFromRowLines'
import { getInvoiceEditorFormValuesFromDraft } from '../../utils/invoiceDraft/getInvoiceEditorFormValuesFromDraft'
import { getInvoiceEditorFormValuesFromSourceDocument } from '../../utils/invoiceDraft/getInvoiceEditorFormValuesFromSourceDocument'
import { getQuoteIdFromDraft } from '../../utils/invoiceDraft/getQuoteIdFromDraft'
import { getInvoiceFormDefaultValues } from '../../utils/invoiceEditorForm/getInvoiceFormDefaultValues'
import { getVatExemptedFormValues } from '../../utils/invoiceEditorForm/getVatExemptedFormValues'
import { useFetchSalesDocumentDraft } from '../useFetchSalesDocumentDraft'
import { useInvoiceEditorDocumentType } from '../useInvoiceEditorDocumentType'
import { useInvoiceEditorVariant } from '../useInvoiceEditorVariant'
import { useGetEditorValuesCommonData } from './useGetEditorValuesCommonData'

interface UseGetInvoiceEditorValuesOptions {
  // Switched off for the recurring editor variant, which loads its values through
  // useGetRecurringInvoiceEditorValues instead.
  // Both hooks are always called (see InvoiceEditorValuesProvider) so the disabled one
  // must not issue any requests.
  enabled: boolean
}

export const useGetInvoiceEditorValues = ({ enabled }: UseGetInvoiceEditorValuesOptions) => {
  const { t } = useTranslation()
  const {
    defaultContact,
    documentId,
    isLoading: isLoadingValuesData,
    isStripeCardPaymentActive,
    organization,
    predefinedInvoiceValues,
  } = useGetEditorValuesCommonData({ enabled })

  const { depositConfig, editedDepositConfig } = useInvoiceEditorDepositModal()
  const { vatRates } = useVatRates()
  const { getFilteredVatRates } = useFilteredVatRates()

  const { isCreditNote, isQuote, documentType, sourceDocumentType } = useInvoiceEditorDocumentType()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()

  const isOperatingOnInvoice = enabled && !!documentId && isDocumentTypeInvoice(sourceDocumentType)
  const isOperatingOnQuote = enabled && !!documentId && isDocumentTypeQuote(sourceDocumentType)
  const isOperatingOnSalesDocumentDraft =
    enabled &&
    !!documentId &&
    [InvoiceDocumentType.InvoiceDraft, InvoiceDocumentType.QuoteDraft].includes(sourceDocumentType)
  const isOperatingOnDepositInvoiceDraft = invoiceEditorVariant === InvoiceEditorVariant.DepositInvoiceDraftEdit

  const { invoice, isLoading: isFetchingInvoice } = useFetchInvoice(
    InvoiceDocumentType.Invoice,
    isOperatingOnInvoice ? documentId : undefined,
  )

  const { salesDocumentDraft, isLoading: isFetchingSalesDocumentDraft } = useFetchSalesDocumentDraft(
    documentType,
    isOperatingOnSalesDocumentDraft ? documentId : undefined,
  )

  const depositDraftQuoteId = isOperatingOnDepositInvoiceDraft ? getQuoteIdFromDraft(salesDocumentDraft) : undefined

  const { quote, isLoading: isFetchingQuote } = useFetchQuote(isOperatingOnQuote ? documentId : depositDraftQuoteId)

  const isLoadingInvoice = isOperatingOnInvoice ? isFetchingInvoice : false
  const isLoadingQuote = isOperatingOnQuote || isOperatingOnDepositInvoiceDraft ? isFetchingQuote : false
  const isLoadingSalesDocumentDraft = isOperatingOnSalesDocumentDraft ? isFetchingSalesDocumentDraft : false
  const isLoading = isLoadingInvoice || isLoadingQuote || isLoadingValuesData || isLoadingSalesDocumentDraft

  const shouldLoadInvoiceValues = isOperatingOnInvoice && !!invoice
  const shouldLoadQuoteValues = isOperatingOnQuote && !!quote
  const shouldLoadSalesDocumentDraftValues = isOperatingOnSalesDocumentDraft && !!salesDocumentDraft

  const invoiceValues = useMemo(() => {
    // nothing consumes these while disabled, so skip loading the document and resolving
    // the predefined values into it
    if (!enabled) {
      return getInvoiceFormDefaultValues()
    }

    const defaultInvoiceValues = getInvoiceFormDefaultValues({
      predefinedInvoiceValues,
      defaultContact,
      isQuote,
      isStripeCardPaymentActive,
    })

    let values = defaultInvoiceValues

    if (shouldLoadInvoiceValues) {
      values = getInvoiceEditorFormValuesFromSourceDocument(invoice, defaultInvoiceValues, {
        getFilteredVatRates,
        isCreditNote,
        vatRates,
      })
    } else if (shouldLoadQuoteValues) {
      values = getInvoiceEditorFormValuesFromSourceDocument(quote, defaultInvoiceValues, {
        getFilteredVatRates,
        isQuote,
        preserveSourceIdentity: invoiceEditorVariant === InvoiceEditorVariant.QuoteEdit,
        vatRates,
      })

      if (invoiceEditorVariant === InvoiceEditorVariant.DepositInvoiceNewFromQuote && depositConfig) {
        values.invoiceLines = getDepositLinesFromRowLines({
          depositValue: depositConfig?.amount || 0,
          depositValueType: depositConfig?.type || DepositInvoiceAmountType.Percentage,
          quote,
          t,
        })
        // The quote's discount is already baked into its discounted total excl. VAT, which the
        // deposit amount is calculated from - carrying the inherited discount forward here would
        // make getFieldsPayload send discount metadata for an amount that isn't actually discounted.
        values.discount = 0
      }
    } else if (shouldLoadSalesDocumentDraftValues) {
      values = getInvoiceEditorFormValuesFromDraft({
        draft: salesDocumentDraft,
        defaultInvoiceValues,
        getFilteredVatRates,
        isQuote,
        vatRates,
      })

      if (isOperatingOnDepositInvoiceDraft) {
        if (editedDepositConfig && quote) {
          // User edited the deposit this session - recompute against the quote
          // (safe: the quote stays locked once a deposit draft exists).
          values.invoiceLines = getDepositLinesFromRowLines({
            depositValue: editedDepositConfig.amount,
            depositValueType: editedDepositConfig.type,
            quote,
            t,
          })
        } else {
          // On load, draft lines lost their deposit marker when saved - re-tag them
          // using the saved amount type, without recomputing.
          const persistedAmountType = salesDocumentDraft.fields.deposit?.value.amountType

          if (persistedAmountType) {
            values.invoiceLines = getDepositLinesFromPersistedRowLines(values.invoiceLines, persistedAmountType)
          }
        }
      }
    }

    if (invoiceEditorFromQuoteVariants.includes(invoiceEditorVariant) && !!documentId) {
      values.approvedEstimateId = documentId
    }

    if (
      invoiceEditorVariant === InvoiceEditorVariant.InvoiceDraftDuplicate ||
      invoiceEditorVariant === InvoiceEditorVariant.QuoteDraftDuplicate
    ) {
      values.id = undefined
    }

    if (organization?.isVatExempted && !isCreditNote) {
      return getVatExemptedFormValues(values, defaultInvoiceValues)
    }

    return values
  }, [
    defaultContact,
    depositConfig,
    documentId,
    editedDepositConfig,
    enabled,
    getFilteredVatRates,
    invoice,
    invoiceEditorVariant,
    isCreditNote,
    isOperatingOnDepositInvoiceDraft,
    isQuote,
    isStripeCardPaymentActive,
    organization?.isVatExempted,
    predefinedInvoiceValues,
    quote,
    salesDocumentDraft,
    shouldLoadInvoiceValues,
    shouldLoadQuoteValues,
    shouldLoadSalesDocumentDraftValues,
    t,
    vatRates,
  ])

  return { invoiceValues, isLoading, predefinedInvoiceValues }
}
